import {batch} from 'react-redux';
import _ from 'lodash';

import {resolveIntl} from './commonBusiness';
import * as labwareStateAction from '../actions/labwareStateAction';
import * as modalStateAction from '../actions/modalStateAction';
import {showSnackbar} from '../actions/appStateAction';
import * as cardStateAction from '../actions/cardStateAction';
import * as cardSerialDilutionStateAction from '../actions/cardSerialDilutionStateAction';
import * as taskListBusinessAction from './taskListBusinessAction';
import * as protocolManagementBusiness from './protocolManagementBusiness';
import {saveState} from './stateBusiness';
import {globalAddUndoItem} from './undoRedoBusinessAction';

import {find, filterToArray, filter, isEmpty, selectDistinct} from '../../lib/objectHelper';
import {incrementName} from '../../lib/stringHelper';
import {isColorLight} from '../../lib/colorHelper';
import jsonCopy from '../../lib/jsonCopy';

import {
  Feedback,
  RuntimeMode,
  TaskType,
  CardType,
  WellCount,
  TipChannelType,
  SerialDilutionStep,
  TrayType,
  NormalizationStep,
  NormalizationType,
  ProtocolEditorViewMode,
} from '../../constants/dataEnum';
import {
  convertRowToNumeric,
  getQuadrant,
  isLabware384,
  isLabwareTrough,
  isSameLabwareHash,
} from '../../contents/ProtocolEditor/VirtualTray/labwareHelper';
import {handlePlateSwapDuplicateInBroadcast, handlePlateSwapMergeInBroadcast} from './taskListBroadcastBusiness';
import {handlePlateSwapDuplicateInPooling, handlePlateSwapMergeInPooling} from './taskListPoolingBusiness';
import {handlePlateSwapDuplicateInMixing} from './taskListMixingBusiness';
import {convertRowToAlphabet} from './commonTaskListBusiness';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import {selectNormalizationDiluentPlate, selectNormalizationSamplePlate} from './taskListNormalizationBusinessAction';

const intl = resolveIntl();

const runtimeDoneDispenseWellColor = '#00FF66';
const runtimeAspirateWellColor = '#F89723';
const runtimeDispenseWellColor = '#3771C8';
const unhighlightedTaskWellColor = '#858585';
const normalizationTaskColor = '#6ABC6D';

// eslint-disable-next-line default-param-last
export function setDeckLabware(targetLabwarePos, labware, sourceLabwarePos = 0, defaultLabwareName) {
  return async (dispatch, getState) => {
    const {
      labwareState,
      cardState,
      availableLabwareState,
      protocolState,
      normalizationState,
      protocolEditorState: {selectedMenu: protocolEditorView},
    } = getState();
    const {tasks, cards} = cardState;
    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;
    let showSucceedSnackbar = true;

    const targetLabware = labwareState[targetLabwarePos];

    if (sourceLabwarePos) {
      if (protocolTrayType === TrayType.HIGH_VOLUME_CADDY && (targetLabwarePos === 5 || sourceLabwarePos === 5)) {
        const checkUpdateTask = (tasklist, prevLabwarePos, trgtLabwarePos, prevLabware) => {
          tasklist.forEach((task) => {
            if (task.type === TaskType.ASPIRATE || task.type === TaskType.DISPENSE) {
              const newTask = jsonCopy(task);
              const prevMinTipPosition = dispatch(
                protocolManagementBusiness.getMinTipPositionByLabwarePrefixId(prevLabware.IDPrefix, prevLabwarePos),
              );
              const minTipPosition = dispatch(
                protocolManagementBusiness.getMinTipPositionByLabwarePrefixId(prevLabware.IDPrefix, trgtLabwarePos),
              );

              let updateTheTask = false;

              if (newTask.tipPosition < minTipPosition || task.tipPosition === prevMinTipPosition) {
                newTask.tipPosition = minTipPosition;
                updateTheTask = true;
              }

              if (
                newTask.mixingPositionAspirate < minTipPosition ||
                task.mixingPositionAspirate === prevMinTipPosition
              ) {
                newTask.mixingPositionAspirate = minTipPosition;
                updateTheTask = true;
              }

              if (
                newTask.mixingPositionDispense < minTipPosition ||
                task.mixingPositionDispense === prevMinTipPosition
              ) {
                newTask.mixingPositionDispense = minTipPosition;
                updateTheTask = true;
              }

              if (updateTheTask) {
                dispatch(cardStateAction.restoreCardTaskStateById(newTask));

                if (trgtLabwarePos === 5) {
                  showSucceedSnackbar = false;

                  dispatch(
                    showSnackbar(
                      Feedback.WARNING,
                      <FormattedHTMLMessage
                        id="protocolEditor.taskList.pipettingProfile.setToDefaultDeck5"
                        values={{tipPosition: minTipPosition}}
                      />,
                    ),
                  );
                }
              }
            }
          });
        };

        const taskOnSourceLabware = Object.values(tasks).filter((e) => e.labwareID === labware.LabwareID);
        const taskOnTargetLabware = Object.values(tasks).filter((e) => e.labwareID === targetLabware.LabwareID);

        checkUpdateTask(taskOnSourceLabware, sourceLabwarePos, targetLabwarePos, labware);
        checkUpdateTask(taskOnTargetLabware, targetLabwarePos, sourceLabwarePos, targetLabware);
      }

      dispatch(labwareStateAction.switchLabware(targetLabwarePos, sourceLabwarePos));

      dispatch(saveState());

      return showSucceedSnackbar;
    }

    let labwareId = targetLabware.LabwareID;
    const isRenamed = defaultLabwareName ? true : (targetLabware?.isRenamed ?? false);
    let labwareName = defaultLabwareName ?? targetLabware.name;

    const referenceLabware = find(availableLabwareState, {IDPrefix: labware.IDPrefix});
    const originalName = referenceLabware?.name ?? '';
    const barcode = targetLabware?.barcode;

    batch(() => {
      if (targetLabware.IDPrefix !== labware.IDPrefix) {
        const existingLabwares = filterToArray(labwareState).filter((e) => e && e.name);

        const existingLabwareNames = existingLabwares.map((e) => e.name);
        labwareName = labwareName && isRenamed ? labwareName : incrementName(labware.name, existingLabwareNames);

        const existingLabwareIdsWithSamePrefix = existingLabwares
          .filter((e) => e.IDPrefix?.toString() === labware.IDPrefix.toString())
          .map((e) => e.LabwareID);

        let counter = 0;

        do {
          labwareId = `${labware.IDPrefix}${String(counter).padStart(2, '0')}`;
          counter += 1;
        } while (existingLabwareIdsWithSamePrefix.includes(labwareId));

        const oldTasks = targetLabware.LabwareID ? filterToArray(tasks, {labwareID: targetLabware.LabwareID}) : [];

        let isShowSnackbar = false;
        let minTipPosition = 1;

        for (let i = 0, ii = oldTasks.length; i < ii; i += 1) {
          if (oldTasks[i].type !== TaskType.DELAY && oldTasks[i].type !== TaskType.USER_CONFIRMATION) {
            const newTask = oldTasks[i];
            newTask.labwareID = labwareId;

            const prevMinTipPosition = dispatch(
              protocolManagementBusiness.getMinTipPositionByLabwarePrefixId(targetLabware.IDPrefix, targetLabwarePos),
            );
            minTipPosition = dispatch(
              protocolManagementBusiness.getMinTipPositionByLabwarePrefixId(labware.IDPrefix, targetLabwarePos),
            );
            const maxTipPosisiton = parseFloat((Math.floor((labware?.wellDepth ?? 0) * 100) / 100).toFixed(2));

            if (
              minTipPosition !== prevMinTipPosition &&
              (minTipPosition > 1 || newTask.tipPosition === prevMinTipPosition)
            ) {
              if (newTask.tipPosition < minTipPosition || newTask.tipPosition === prevMinTipPosition) {
                newTask.tipPosition = minTipPosition;
              }

              if (
                newTask.mixingPositionAspirate < minTipPosition ||
                newTask.mixingPositionAspirate === prevMinTipPosition
              ) {
                newTask.mixingPositionAspirate = minTipPosition;
              }

              if (
                newTask.mixingPositionDispense < minTipPosition ||
                newTask.mixingPositionDispense === prevMinTipPosition
              ) {
                newTask.mixingPositionDispense = minTipPosition;
              }

              isShowSnackbar = true;
            }

            if (newTask.tipPosition > maxTipPosisiton) {
              newTask.tipPosition = minTipPosition;
              isShowSnackbar = true;
            }
            if (newTask.mixingPositionAspirate > maxTipPosisiton) {
              newTask.mixingPositionAspirate = minTipPosition;
              isShowSnackbar = true;
            }

            if (newTask.mixingPositionDispense > maxTipPosisiton) {
              newTask.mixingPositionDispense = minTipPosition;
              isShowSnackbar = true;
            }

            dispatch(cardStateAction.restoreCardTaskStateById(newTask));
          }
        }

        if (isShowSnackbar) {
          showSucceedSnackbar = false;
          dispatch(
            showSnackbar(
              Feedback.WARNING,
              <FormattedHTMLMessage
                id={`protocolEditor.taskList.pipettingProfile.${
                  protocolTrayType === TrayType.HIGH_VOLUME_CADDY && targetLabwarePos === 5
                    ? 'setToDefaultDeck5'
                    : 'setToDefault'
                }`}
                values={{tipPosition: minTipPosition}}
              />,
            ),
          );
        }

        const sdCards = filterToArray(cards, {type: CardType.SERIAL_DILUTION});

        for (let i = 0, ii = sdCards.length; i < ii; i += 1) {
          const sdc = jsonCopy(sdCards[i]);
          const sdRawTasks = filterToArray(sdc.sdRawTasks, {labwareID: targetLabware.LabwareID});

          for (let j = 0, jj = sdRawTasks.length; j < jj; j += 1) {
            const sdRawTask = sdRawTasks[j];
            sdRawTask.labwareID = labwareId;

            dispatch(cardSerialDilutionStateAction.restoreCardSdRawTaskStateById(sdc.ID, sdRawTask));
          }
        }

        const normalizationCards = Object.values(cards).filter((card) => card.type === CardType.NORMALIZATION);
        for (let i = 0, ii = normalizationCards.length; i < ii; i += 1) {
          const nc = normalizationCards[i];
          const ns = nc.normalizationState;
          const nSelectedLabware = ns.sampleLabwareID;
          const nDiluentLabware = ns.diluentLabwareID;

          let updateNormalizationState = false;
          const newNormalizationState = {...ns};

          if (nSelectedLabware === targetLabware.LabwareID) {
            newNormalizationState.sampleLabwareID = labwareId;
            updateNormalizationState = true;
          }

          if (nDiluentLabware === targetLabware?.LabwareID) {
            newNormalizationState.diluentLabwareID = labwareId;
            updateNormalizationState = true;
          }

          if (updateNormalizationState) {
            dispatch(cardStateAction.saveNormalizationStateToCard(nc.ID, newNormalizationState));
          }
        }

        if (protocolEditorView === ProtocolEditorViewMode.NORMALIZATION) {
          const newLabware = {
            ...labware,
            isSelected: false,
            LabwareID: labwareId,
            name: labwareName,
            originalName,
            isRenamed,
            barcode,
          };

          if (normalizationState.sampleLabwareID === targetLabware.LabwareID) {
            dispatch(selectNormalizationSamplePlate(newLabware));
          } else if (normalizationState.diluentLabwareID === targetLabware.LabwareID) {
            dispatch(selectNormalizationDiluentPlate(newLabware));
          }
        }
      }

      dispatch(
        labwareStateAction.setLabware(targetLabwarePos, {
          ...labware,
          isSelected: false,
          LabwareID: labwareId,
          name: labwareName,
          originalName,
          isRenamed,
          barcode,
        }),
      );
      dispatch(taskListBusinessAction.smarterDefaultDispenseTipPosition([labwareId]));
      dispatch(saveState());
    });
    return showSucceedSnackbar;
  };
}

export function getDeckLabwareName(labwarePos) {
  return (dispatch, getState) => {
    const {labwareState} = getState();

    return labwareState[labwarePos]?.name;
  };
}

export function getDeckLabwareNameById(labwareId) {
  return (dispatch, getState) => {
    const {labwareState} = getState();

    return find(labwareState, {LabwareID: labwareId})?.name;
  };
}

export function selectLabware(labwarePos) {
  return (dispatch, getState) => {
    const {labwareState} = getState();
    if (labwareState[labwarePos]?.name && !labwareState[labwarePos]?.isSelected) {
      dispatch(labwareStateAction.selectLabware(labwarePos));
    }
  };
}

export function checkAndRemoveLabware(labwarePos, afterDelete = () => {}) {
  return (dispatch, getState) => {
    const {cardState, labwareState} = getState();
    const {tasks, cards} = cardState;
    const {LabwareID} = labwareState[labwarePos];

    const sdCards = filterToArray(cards, {type: CardType.SERIAL_DILUTION});
    const sdRawTasks = sdCards.map((item) => item.sdRawTasks);
    const sdRawTaskList = sdRawTasks.length ? sdRawTasks?.reduce((acc, obj) => ({...acc, ...Object.values(obj)})) : {};

    const isLabwareUsedOnTask = find(tasks, {labwareID: LabwareID});
    const isLabwareUsedOnSDTask = find(sdRawTaskList, {labwareID: LabwareID});

    if (!isLabwareUsedOnTask && !isLabwareUsedOnSDTask) {
      dispatch(
        modalStateAction.showModal(
          intl.formatMessage({
            id: 'labwareBusinessAction.checkAndRemoveLabware.removeLabware.title',
          }),
          intl.formatMessage({
            id: 'labwareBusinessAction.checkAndRemoveLabware.removeLabware.content',
          }),
          [
            {
              className: 'btn-lg',
              style: 'primary',
              handlerClick: () => {
                batch(() => {
                  dispatch(labwareStateAction.removeLabware(labwarePos));
                  afterDelete();
                  dispatch(globalAddUndoItem());
                  dispatch(saveState());
                  dispatch(modalStateAction.hideModal());
                });
              },
              text: intl.formatMessage({id: 'common.modal.action.yes'}),
            },
            {
              className: 'btn-lg',
              style: 'secondary',
              handlerClick: () => {
                dispatch(modalStateAction.hideModal());
              },
              text: intl.formatMessage({id: 'common.modal.action.no'}),
            },
          ],
          () => {},
          '',
          true,
        ),
      );
    } else {
      dispatch(
        showSnackbar(
          Feedback.ERROR,
          intl.formatMessage({
            id: 'labwareBusinessAction.checkAndRemoveLabware.labwareBeingUsed',
          }),
          4000,
        ),
      );
    }
  };
}

export const renameLabware =
  (key, newName, saveUndo = true) =>
  (dispatch, getState) => {
    const state = getState();
    const {labwareState} = state;

    const labwareKeys = Object.keys(labwareState).filter((e) => e.toString() === key.toString());

    if (!labwareKeys.length || labwareState[labwareKeys[0]].name === newName) {
      return;
    }

    const labwareNames = [];

    Object.keys(labwareState)
      .filter((e) => e.toString() !== key.toString())
      .forEach((e) => {
        const labwareName = labwareState[e].name;

        if (labwareName) {
          labwareNames.push(labwareName);
        }
      });

    const newValidName = incrementName(newName, labwareNames);

    dispatch(labwareStateAction.renameLabware(key, newValidName));
    if (saveUndo) {
      dispatch(globalAddUndoItem());
    }
    dispatch(saveState());
  };

const getTextColor = (colorHex) => {
  try {
    return isColorLight(colorHex) ? '#000000' : '#ffffff';
  } catch (err) {
    return '#000000';
  }
};

const getWellStyle = (isSetBorderColor, wellStyleTemp, color, background, opacity) => {
  if (isSetBorderColor) {
    return {
      background: wellStyleTemp?.background || background,
      border: color,
      borderSize: background ? 2 : 3,
      color: getTextColor(wellStyleTemp?.background),
      opacity: opacity || 1,
    };
  }

  return {
    background: background || color,
    border: wellStyleTemp?.border,
    color: getTextColor(background || color),
    opacity: opacity || 1,
  };
};

// eslint-disable-next-line default-param-last
const mergeTaskWellColorMap = (taskWellColorMap = {}, taskColor, labwareID, isSetBorder) => {
  for (let iKey = 0; iKey < Object.keys(taskColor).length; iKey += 1) {
    const wellID = Object.keys(taskColor)[iKey];

    if (wellID in taskWellColorMap[labwareID]) {
      if (isSetBorder) {
        const existingTaskBorder = taskWellColorMap[labwareID][wellID].border;
        const newTaskBorder = taskColor[wellID].border;

        taskWellColorMap[labwareID][wellID].border = !(
          existingTaskBorder && newTaskBorder === unhighlightedTaskWellColor
        )
          ? newTaskBorder
          : existingTaskBorder;
      } else {
        const existingTaskBackground = taskWellColorMap[labwareID][wellID].background;
        const newTaskBackground = taskColor[wellID].background;

        taskWellColorMap[labwareID][wellID].background = !(
          existingTaskBackground && newTaskBackground === unhighlightedTaskWellColor
        )
          ? newTaskBackground
          : existingTaskBackground;
      }
    }
  }

  taskWellColorMap[labwareID] = {
    ...taskColor,
    ...taskWellColorMap[labwareID],
  };

  return taskWellColorMap;
};

export const getWellStyleColorByLabwareWellCount =
  (
    labwareRowCount,
    labwareColumnCount,
    row,
    column,
    color,
    // eslint-disable-next-line default-param-last
    rowTipCount = 0,
    // eslint-disable-next-line default-param-last
    columnTipCount = 0,
    selectedTip,
    // eslint-disable-next-line default-param-last
    isSetBorderColor = false,
    background, // array
    opacity,
    skipWellRowCount,
    skipWellColumnCount,
    ignoreTipMode = false,
    isSkipWell = true,
    isIFC = false,
    labwareIndex = 0,
  ) =>
  () => {
    const rowCount = labwareRowCount;
    const colCount = labwareColumnCount;
    const labwareWellCount = colCount * rowCount;

    const wellStyleTemp = {};
    let skipWell = 0;
    let startingWellRow = 1;
    let startingWellCol = 1;

    const is384 = labwareWellCount === WellCount.WELL_384;

    if ((is384 || isIFC) && isSkipWell) {
      skipWell = 1;
      startingWellRow = row % 2 !== 0 ? 1 : 2;
      startingWellCol = column % 2 !== 0 ? 1 : 2;
    }

    const skipWellRow = skipWellRowCount ?? skipWell;
    const skipWellColumn = skipWellColumnCount ?? skipWell;

    let tipMode = selectedTip;

    if (!ignoreTipMode && columnTipCount && rowTipCount) {
      if (columnTipCount === 1 && rowTipCount === 1) {
        tipMode = TipChannelType.TIP_CHANNEL_1;
      } else if (columnTipCount === 1 && rowTipCount === 8) {
        tipMode = TipChannelType.TIP_CHANNEL_8;
      } else if (columnTipCount === 12 && rowTipCount === 1) {
        tipMode = TipChannelType.TIP_CHANNEL_12;
      } else if (columnTipCount === 12 && rowTipCount === 8) {
        tipMode = TipChannelType.TIP_CHANNEL_96;
      }
    }

    switch (tipMode) {
      case TipChannelType.TIP_CHANNEL_1: {
        const wellID = `${row}-${column}-${labwareIndex}`;
        wellStyleTemp[wellID] = getWellStyle(isSetBorderColor, wellStyleTemp[wellID], color, background, opacity);

        break;
      }
      case TipChannelType.TIP_CHANNEL_8: {
        for (let index = startingWellRow; index <= rowCount; index += 1 + skipWellRow) {
          const wellID = `${index}-${column}-${labwareIndex}`;
          wellStyleTemp[wellID] = getWellStyle(isSetBorderColor, wellStyleTemp[wellID], color, background, opacity);
        }

        break;
      }
      case TipChannelType.TIP_CHANNEL_12: {
        for (let index = startingWellCol; index <= colCount; index += 1 + skipWellColumn) {
          const wellID = `${row}-${index}-${labwareIndex}`;
          wellStyleTemp[wellID] = getWellStyle(isSetBorderColor, wellStyleTemp[wellID], color, background, opacity);
        }

        break;
      }
      case TipChannelType.TIP_CHANNEL_96: {
        for (let iRow = startingWellRow; iRow <= rowCount; iRow += 1 + skipWellRow) {
          for (let iCol = startingWellCol; iCol <= colCount; iCol += 1 + skipWellColumn) {
            const wellID = `${iRow}-${iCol}-${labwareIndex}`;
            wellStyleTemp[wellID] = getWellStyle(isSetBorderColor, wellStyleTemp[wellID], color, background, opacity);
          }
        }

        break;
      }
      default: {
        const rowIncrementor = skipWellRow + 1;
        const columnIncrementor = skipWellColumn + 1;

        let adjustedRowTipCount = rowTipCount;
        let adjustedColumnTipCount = columnTipCount;

        if (skipWellRow > 0) {
          adjustedRowTipCount = rowTipCount + (rowTipCount - 1) * skipWellRow;
        }

        if (skipWellColumn > 0) {
          adjustedColumnTipCount = columnTipCount + (columnTipCount - 1) * skipWellColumn;
        }

        for (let iRow = row; iRow < row + adjustedRowTipCount; iRow += rowIncrementor) {
          for (let iCol = column; iCol < column + adjustedColumnTipCount; iCol += columnIncrementor) {
            const wellID = `${iRow}-${iCol}-${labwareIndex}`;

            wellStyleTemp[wellID] = getWellStyle(isSetBorderColor, wellStyleTemp[wellID], color, background, opacity);
          }
        }

        break;
      }
    }

    return wellStyleTemp;
  };

export const getSpearBioWellStyleColorByLabwareWellCount =
  (
    labwareRowCount,
    labwareColumnCount,
    row,
    column,
    color,
    // eslint-disable-next-line default-param-last
    rowTipCount = 0,
    // eslint-disable-next-line default-param-last
    columnTipCount = 0,
    selectedTip,
    // eslint-disable-next-line default-param-last
    isSetBorderColor = false,
    background, // array
    opacity,
    skipWellRowCount,
    skipWellColumnCount,
    ignoreTipMode = false,
  ) =>
  () => {
    const rowCount = labwareRowCount;
    const colCount = labwareColumnCount;

    let wellindex = 0;

    const wellStyleTemp = {};
    const skipWell = 0;
    let startingWellRow = row;
    const startingWellCol = 1;
    const LAST_TROUGH_COLUMN = 3;

    if (column <= 9) {
      startingWellRow = 8;
    }

    const skipWellRow = skipWellRowCount ?? skipWell;
    const skipWellColumn = skipWellColumnCount ?? skipWell;

    let tipMode = selectedTip;

    if (!ignoreTipMode && columnTipCount && rowTipCount) {
      if (columnTipCount === 1 && rowTipCount === 1) {
        tipMode = TipChannelType.TIP_CHANNEL_1;
      } else if (columnTipCount === 1 && rowTipCount === 8) {
        tipMode = TipChannelType.TIP_CHANNEL_8;
      } else if (columnTipCount === 12 && rowTipCount === 1) {
        tipMode = TipChannelType.TIP_CHANNEL_12;
      } else if (columnTipCount === 12 && rowTipCount === 8) {
        tipMode = TipChannelType.TIP_CHANNEL_96;
      }
    }

    switch (tipMode) {
      case TipChannelType.TIP_CHANNEL_12: {
        for (let iCol = startingWellCol; iCol <= colCount; iCol += 1 + skipWellColumn) {
          // this is used to make the troughs (with no micro well inside) all have the same row.
          // because all troughs have the same wellID, this wellID will have the latest assigned style value.
          // hence when this func is called when retrieving well color from tasks, the color on troughs that will be shown will be the latest task
          const wellIDRow = iCol > LAST_TROUGH_COLUMN ? row : startingWellRow;
          const wellID = `${wellIDRow}-${iCol}-${wellindex}`;
          wellStyleTemp[wellID] = getWellStyle(isSetBorderColor, wellStyleTemp[wellID], color, background, opacity);
          wellindex += 1;
        }

        break;
      }
      case TipChannelType.TIP_CHANNEL_96: {
        startingWellRow = row;
        for (let iRow = startingWellRow; iRow <= rowCount; iRow += 1 + skipWellRow) {
          wellindex = 0;
          for (let iCol = startingWellCol; iCol <= colCount; iCol += 1 + skipWellColumn) {
            const wellID = `${iRow}-${iCol}-${wellindex}`;
            wellStyleTemp[wellID] = getWellStyle(isSetBorderColor, wellStyleTemp[wellID], color, background, opacity);
            wellindex += 1;
          }
        }
        break;
      }
      default: {
        const rowIncrementor = skipWellRow + 1;
        const columnIncrementor = skipWellColumn + 1;

        let adjustedRowTipCount = rowTipCount;
        let adjustedColumnTipCount = columnTipCount;

        if (skipWellRow > 0) {
          adjustedRowTipCount = rowTipCount + (rowTipCount - 1) * skipWellRow;
        }

        if (skipWellColumn > 0) {
          adjustedColumnTipCount = columnTipCount + (columnTipCount - 1) * skipWellColumn;
        }

        for (let iRow = row; iRow < row + adjustedRowTipCount; iRow += rowIncrementor) {
          wellindex = column - 1;
          for (let iCol = column; iCol < column + adjustedColumnTipCount; iCol += columnIncrementor) {
            const wellID = `${iRow}-${iCol}-${wellindex}`;

            wellStyleTemp[wellID] = getWellStyle(isSetBorderColor, wellStyleTemp[wellID], color, background, opacity);
            wellindex += 1;
          }
        }

        break;
      }
    }

    return wellStyleTemp;
  };

export const getWellStyleColor =
  (
    labwareId,
    row,
    column,
    color,
    // eslint-disable-next-line default-param-last
    rowTipCount = 0,
    // eslint-disable-next-line default-param-last
    columnTipCount = 0,
    selectedTip,
    // eslint-disable-next-line default-param-last
    isSetBorderColor = false,
    background, // array
    opacity,
    skipWellRowCount,
    skipWellColumnCount,
    ignoreTipMode = false,
    isSkipWell = true,
    labwareIndex = 0,
    isManualReteach = false,
  ) =>
  (dispatch, getState) => {
    let labware = {};

    if (!isManualReteach) {
      const {labwareState} = getState();
      labware = find(labwareState, {LabwareID: labwareId});
    } else {
      const {availableLabwareState} = getState();
      labware = find(availableLabwareState, {LabwareID: labwareId});
    }

    if (!labware) {
      return {};
    }

    let {colCount, rowCount} = labware;
    if (labware.isIFC && labwareIndex) {
      colCount = labware.labwareIndexes[labwareIndex].colCount;
      rowCount = labware.labwareIndexes[labwareIndex].rowCount;
    }

    let wellStyleTemp = {};

    if (labware.isSpearBio) {
      wellStyleTemp = dispatch(
        getSpearBioWellStyleColorByLabwareWellCount(
          rowCount,
          colCount,
          row,
          column,
          color,
          rowTipCount,
          columnTipCount,
          selectedTip,
          isSetBorderColor,
          background,
          opacity,
          skipWellRowCount,
          skipWellColumnCount,
          ignoreTipMode,
          labwareIndex,
        ),
      );
    } else {
      wellStyleTemp = dispatch(
        getWellStyleColorByLabwareWellCount(
          rowCount,
          colCount,
          row,
          column,
          color,
          rowTipCount,
          columnTipCount,
          selectedTip,
          isSetBorderColor,
          background,
          opacity,
          skipWellRowCount,
          skipWellColumnCount,
          ignoreTipMode,
          isSkipWell,
          labware.isIFC,
          labwareIndex,
        ),
      );
    }

    return wellStyleTemp;
  };

export const getWellStyleColorMapFromTasks = (cardId, selectedTip, runtimeMode) => (dispatch, getState) => {
  const state = getState();
  const {colorState, labwareState, cardState, runtimeState} = state;
  const {colorPalette} = colorState;
  const {tasks, cards} = cardState;
  const {status, csvRecordID, doneRecordCSVIDs} = runtimeState;

  let taskWellColorMap = {};

  Object.keys(labwareState).forEach((e) => {
    taskWellColorMap[labwareState[e].LabwareID] = {};
  });

  const selectedCard = cards[cardId];

  if (isEmpty(selectedCard)) {
    return taskWellColorMap;
  }

  const taskDiluentSourceId = selectedCard.sdRawTasksSummary?.taskDiluentSource;
  const taskSampleSourceId = selectedCard.sdRawTasksSummary?.taskSampleSource;
  const taskSampleTargetIds = _.isNumber(selectedCard.sdRawTasksSummary?.taskSampleTarget)
    ? [selectedCard.sdRawTasksSummary?.taskSampleTarget]
    : selectedCard.sdRawTasksSummary?.taskSampleTarget || [];
  const taskDiluentTargetIds = selectedCard.sdRawTasksSummary?.taskDiluentTargets || [];
  const diluentTargetStep = selectedCard.sdProperty?.step || 0;

  // eslint-disable-next-line no-unsafe-optional-chaining
  const selectedCardTaskIds = [...selectedCard?.tasks, taskDiluentSourceId].filter((id) => _.isNumber(id));

  for (let i = 0; i < taskSampleTargetIds.length; i += 1) {
    selectedCardTaskIds.push(taskSampleTargetIds[i]);
    for (let j = 0; j < diluentTargetStep; j += 1) {
      selectedCardTaskIds.push(taskDiluentTargetIds[j + i]);
    }
  }

  const selectedCardTasks = filter(tasks, {
    ID: selectedCardTaskIds,
  });

  // #region runtime highlight well

  if (runtimeMode) {
    if (isEmpty(selectedCardTasks)) return {};

    if (status !== RuntimeMode.FINISH) {
      const selectedTasks = find(selectedCardTasks, (tsk) => tsk.ID === csvRecordID);

      if (selectedTasks) {
        if (selectedTasks.labwareID === undefined) return {};
        const {row, column, columnTipCount, rowTipCount, type, labwareID, labwareIndex} = selectedTasks;
        const selectedLabware = find(labwareState, {LabwareID: labwareID});

        if (type === TaskType.ASPIRATE || type === TaskType.DISPENSE || type === TaskType.MIXING) {
          const currentAspirateTaskColor = dispatch(
            getWellStyleColor(
              labwareID,
              row.charCodeAt(0) - 64,
              column,
              type === TaskType.ASPIRATE ? runtimeAspirateWellColor : runtimeDispenseWellColor,
              rowTipCount,
              columnTipCount,
              selectedTip,
              null,
              null,
              null,
              null,
              null,
              null,
              true,
              selectedLabware.isSpearBio ? column - 1 : labwareIndex,
            ),
          );
          taskWellColorMap[labwareID] = {
            ...currentAspirateTaskColor,
            ...taskWellColorMap[labwareID],
          };
        }
      }
    }

    for (let index = 0; index < doneRecordCSVIDs.length; index += 1) {
      const doneDispenseTaskID = doneRecordCSVIDs[index];
      const alreadyDoneTask = find(selectedCardTasks, (tsk) => tsk.ID === doneDispenseTaskID);

      if (!alreadyDoneTask) continue;

      const {row, column, columnTipCount, rowTipCount, type, labwareID, labwareIndex} = alreadyDoneTask;
      const selectedLabware = find(labwareState, {LabwareID: labwareID});

      if (type === TaskType.DISPENSE || type === TaskType.MIXING) {
        const doneDispenseTaskColor = dispatch(
          getWellStyleColor(
            labwareID,
            row.charCodeAt(0) - 64,
            column,
            runtimeDoneDispenseWellColor,
            rowTipCount,
            columnTipCount,
            selectedTip,
            null,
            null,
            null,
            null,
            null,
            null,
            true,
            selectedLabware.isSpearBio ? column - 1 : labwareIndex,
          ),
        );
        taskWellColorMap[labwareID] = {
          ...doneDispenseTaskColor,
          ...taskWellColorMap[labwareID],
        };
      }
    }

    return taskWellColorMap;
  }

  // #endregion runtime highlight well

  const hasSelectedTask = find(selectedCardTasks, {isSelected: true});
  let prevTaskIsAspirate = false;
  let toDispenseAspirateTaskColorIds = [];
  const processedAspirateWellMap = {};
  const processedDispenseWellMap = {};
  let tipVolume = 0;
  let getTaskColor;

  switch (selectedCard.type) {
    case CardType.SERIAL_DILUTION: {
      const hasSelectedSdRawTask = find(selectedCard.sdRawTasks, {isSelected: true});
      // sample source
      if (_.isNumber(taskSampleSourceId)) {
        const selectedTask = selectedCard.sdRawTasks[taskSampleSourceId];

        if (!selectedTask) {
          return null;
        }

        if (!selectedTask.skipped) {
          const {row, column, rowTipCount, columnTipCount, labwareID, type} = selectedTask;

          const isSetBorder = type === TaskType.ASPIRATE;

          let colorHex = colorPalette[0 % colorPalette.length];

          if (hasSelectedSdRawTask && hasSelectedSdRawTask.number !== SerialDilutionStep.SET_SAMPLE_SOURCE) {
            colorHex = unhighlightedTaskWellColor;
          }

          getTaskColor = dispatch(
            getWellStyleColor(
              labwareID,
              row.charCodeAt(0) - 64,
              column,
              colorHex,
              rowTipCount,
              columnTipCount,
              selectedTip,
              isSetBorder,
              null,
            ),
          );

          taskWellColorMap = mergeTaskWellColorMap(taskWellColorMap, getTaskColor, labwareID, isSetBorder);
        }
      }

      // Diluent Source
      if (_.isNumber(taskDiluentSourceId)) {
        const selectedTask = selectedCard.sdRawTasks[taskDiluentSourceId];

        if (!selectedTask) {
          return null;
        }

        if (!selectedTask.skipped) {
          const {row, column, rowTipCount, columnTipCount, labwareID, type} = selectedTask;

          const isSetBorder = type === TaskType.ASPIRATE;

          let colorHex = colorPalette[1 % colorPalette.length];

          if (hasSelectedSdRawTask && hasSelectedSdRawTask.number !== SerialDilutionStep.SET_DILUENT_SOURCE) {
            colorHex = unhighlightedTaskWellColor;
          }

          getTaskColor = dispatch(
            getWellStyleColor(
              labwareID,
              row.charCodeAt(0) - 64,
              column,
              colorHex,
              rowTipCount,
              columnTipCount,
              selectedTip,
              isSetBorder,
              null,
            ),
          );

          taskWellColorMap = mergeTaskWellColorMap(taskWellColorMap, getTaskColor, labwareID, isSetBorder);
        }
      }

      // Sample Target
      if (taskSampleTargetIds?.length) {
        for (let i = 0; i < taskSampleTargetIds.length; i += 1) {
          const taskSampleTargetId = taskSampleTargetIds[i];

          const selectedTask = selectedCard.sdRawTasks[taskSampleTargetId];

          if (!selectedTask) {
            return null;
          }

          if (!selectedTask.skipped) {
            const {row, column, rowTipCount, columnTipCount, labwareID, type} = selectedTask;

            const isSetBorder = type === TaskType.ASPIRATE;

            let colorHex = colorPalette[0 % colorPalette.length];

            if (hasSelectedSdRawTask && hasSelectedSdRawTask.number !== SerialDilutionStep.SET_SAMPLE_TARGET) {
              colorHex = unhighlightedTaskWellColor;
            }

            getTaskColor = dispatch(
              getWellStyleColor(
                labwareID,
                row.charCodeAt(0) - 64,
                column,
                colorHex,
                rowTipCount,
                columnTipCount,
                selectedTip,
                isSetBorder,
                null,
              ),
            );

            taskWellColorMap = mergeTaskWellColorMap(taskWellColorMap, getTaskColor, labwareID, isSetBorder);
          }
        }
      }

      // Diluent Target
      if (taskDiluentTargetIds?.length) {
        let serialDilutionOpacity = 1;
        const serialDilutionInterval = (1 - 0.3) / diluentTargetStep;
        const opacityMap = {};

        for (let i = 0, ii = taskDiluentTargetIds.length; i < ii; i += 1) {
          const taskId = taskDiluentTargetIds[i];
          const selectedTask = selectedCard.sdRawTasks[taskId];

          if (!selectedTask) {
            continue;
          }

          const {row, column, rowTipCount, columnTipCount, labwareID, type} = selectedTask;
          const labware = find(labwareState, {LabwareID: labwareID});
          const is384 = isLabware384(labware);

          let skipWellRowCount = null;
          let skipWellColumnCount = null;

          if (is384 && rowTipCount > labware.rowCount / 2) {
            skipWellRowCount = 0;
          }
          if (is384 && columnTipCount > labware.colCount / 2) {
            skipWellColumnCount = 0;
          }

          const isSetBorder = type === TaskType.ASPIRATE;

          if (!opacityMap[selectedTask.step]) {
            serialDilutionOpacity -= serialDilutionInterval;
            opacityMap[selectedTask.step] = serialDilutionOpacity;
          }

          let colorHex = colorPalette[0 % colorPalette.length];

          if (hasSelectedSdRawTask && hasSelectedSdRawTask.number !== SerialDilutionStep.SET_DILUENT_TARGET) {
            colorHex = unhighlightedTaskWellColor;
          }

          getTaskColor = dispatch(
            getWellStyleColor(
              labwareID,
              row.charCodeAt(0) - 64,
              column,
              colorHex,
              rowTipCount,
              columnTipCount,
              selectedTip,
              null,
              null,
              opacityMap[selectedTask.step],
              skipWellRowCount,
              skipWellColumnCount,
            ),
          );

          taskWellColorMap = mergeTaskWellColorMap(taskWellColorMap, getTaskColor, labwareID, isSetBorder);
        }
      }
      break;
    }
    default:
      {
        const unmergedTaskWellColorMap = {};
        const selectedCardTaskKeys = Object.keys(selectedCardTasks);
        const dispenseWithBorder = [];

        for (let idx = 0; idx < selectedCardTaskKeys.length; idx += 1) {
          const iTask = selectedCardTaskKeys[idx];
          if (selectedCardTasks[iTask].labwareID === undefined) continue;

          const {row, column, columnTipCount, rowTipCount, type, labwareID, volume, isSelected, labwareIndex} =
            selectedCardTasks[iTask];

          const isSetBorder =
            type === TaskType.ASPIRATE || (selectedCard.type === CardType.BROADCAST && type === TaskType.DISPENSE);
          const selectedLabware = find(labwareState, {LabwareID: labwareID});

          if (
            !selectedLabware ||
            (type !== TaskType.ASPIRATE && type !== TaskType.DISPENSE && type !== TaskType.MIXING)
          ) {
            continue;
          }

          let colorIds = null;
          const wellKey = `${row}_${column}_${selectedLabware.isSpearBio ? column - 1 : labwareIndex}_${labwareID}`;
          let sameWellTask = null;

          if (type === TaskType.ASPIRATE) {
            sameWellTask = processedAspirateWellMap[wellKey];

            if (toDispenseAspirateTaskColorIds.length === 0) {
              toDispenseAspirateTaskColorIds.push(0);
              colorIds = [0];
            } else if (sameWellTask) {
              colorIds = toDispenseAspirateTaskColorIds;
            } else if (prevTaskIsAspirate) {
              colorIds = [toDispenseAspirateTaskColorIds[toDispenseAspirateTaskColorIds.length - 1] + 1];
              toDispenseAspirateTaskColorIds.push(colorIds[0]);
            } else if (tipVolume > 0) {
              toDispenseAspirateTaskColorIds.push(
                toDispenseAspirateTaskColorIds[toDispenseAspirateTaskColorIds.length - 1] + 1,
              );
              colorIds = [toDispenseAspirateTaskColorIds[toDispenseAspirateTaskColorIds.length - 1]];
            } else {
              toDispenseAspirateTaskColorIds = [
                toDispenseAspirateTaskColorIds[toDispenseAspirateTaskColorIds.length - 1] + 1,
              ];
              colorIds = toDispenseAspirateTaskColorIds;
            }

            prevTaskIsAspirate = true;
            processedAspirateWellMap[wellKey] = colorIds;
            tipVolume += volume;
          } else if (type === TaskType.DISPENSE || type === TaskType.MIXING) {
            sameWellTask = processedDispenseWellMap[wellKey];
            let newColorIds = [];

            if (sameWellTask) {
              newColorIds = sameWellTask;
            }

            toDispenseAspirateTaskColorIds.forEach((e) => {
              const colorExist = newColorIds.filter((ee) => ee === e).length > 0;

              if (!colorExist) {
                newColorIds.push(e);
              }
            });

            colorIds = newColorIds;
            prevTaskIsAspirate = false;
            processedDispenseWellMap[wellKey] = colorIds;
            tipVolume -= volume;
            if (isSetBorder) {
              dispenseWithBorder.push(wellKey);
            }

            if (type === TaskType.MIXING || (type === TaskType.DISPENSE && !toDispenseAspirateTaskColorIds.length)) {
              colorIds = [0];
            }
          }

          tipVolume = parseFloat(parseFloat(tipVolume).toFixed(2));
          let colorHex =
            selectedCard.type === CardType.NORMALIZATION
              ? normalizationTaskColor
              : colorPalette[colorIds[0] % colorPalette.length];
          let background =
            colorIds.length <= 1
              ? null
              : colorIds.map((e) => ({
                  background: colorPalette[e % colorPalette.length],
                  volume: 1,
                }));

          if (isSetBorder && type === TaskType.DISPENSE) {
            background = colorHex;
            colorHex = colorPalette[(colorIds.length + dispenseWithBorder.length - 1) % colorPalette.length];
          }

          if (colorIds === null || (hasSelectedTask && !isSelected)) {
            if (selectedCard.type === CardType.NORMALIZATION && labwareID === hasSelectedTask?.labwareID) {
              colorHex = normalizationTaskColor;
              background = type === TaskType.DISPENSE ? normalizationTaskColor : null;
            } else {
              colorHex = unhighlightedTaskWellColor;
              background = type === TaskType.DISPENSE ? unhighlightedTaskWellColor : null;
            }
          }

          getTaskColor = dispatch(
            getWellStyleColor(
              labwareID,
              row.charCodeAt(0) - 64,
              column,
              colorHex,
              rowTipCount,
              columnTipCount,
              selectedTip,
              isSetBorder,
              background,
              null,
              null,
              null,
              null,
              true,
              selectedLabware.isSpearBio ? column - 1 : labwareIndex,
            ),
          );

          // #region logic for overlapping wells coloring

          if (!(labwareID in unmergedTaskWellColorMap)) {
            unmergedTaskWellColorMap[labwareID] = {};
          }

          Object.keys(getTaskColor).forEach((wellId) => {
            if (!(wellId in unmergedTaskWellColorMap[labwareID])) {
              unmergedTaskWellColorMap[labwareID][wellId] = colorIds;
            } else {
              unmergedTaskWellColorMap[labwareID][wellId] = [
                ...unmergedTaskWellColorMap[labwareID][wellId],
                ...colorIds,
              ];
              unmergedTaskWellColorMap[labwareID][wellId] = selectDistinct(unmergedTaskWellColorMap[labwareID][wellId]);
            }
          });

          if (!isSetBorder) {
            // eslint-disable-next-line no-loop-func
            Object.keys(getTaskColor).forEach((wellId) => {
              const allColorIds = unmergedTaskWellColorMap[labwareID][wellId];

              getTaskColor[wellId].background = [];

              allColorIds.forEach((colorId) => {
                if (hasSelectedTask && !isSelected) {
                  if (selectedCard.type === CardType.NORMALIZATION && labwareID === hasSelectedTask?.labwareID) {
                    getTaskColor[wellId].background = normalizationTaskColor;
                  } else {
                    getTaskColor[wellId].background = unhighlightedTaskWellColor;
                  }
                } else {
                  getTaskColor[wellId].background.push({
                    background:
                      selectedCard.type === CardType.NORMALIZATION
                        ? normalizationTaskColor
                        : colorPalette[colorId % colorPalette.length],
                    volume: 1,
                  });
                }
              });
            });
          }

          // #endregion logic for overlapping wells coloring

          taskWellColorMap = mergeTaskWellColorMap(taskWellColorMap, getTaskColor, labwareID, isSetBorder);
        }
      }
      break;
  }

  return taskWellColorMap;
};

const mergeColorMap = (currentColorMap, colorMap, overideColor) => {
  const newColorMap = {...currentColorMap};

  // eslint-disable-next-line guard-for-in, no-restricted-syntax
  for (const labwareId in colorMap) {
    if (!(labwareId in newColorMap)) {
      newColorMap[labwareId] = {};
    }

    // eslint-disable-next-line guard-for-in, no-restricted-syntax
    for (const wellId in colorMap[labwareId]) {
      const {background, border} = colorMap[labwareId][wellId];

      if (overideColor) {
        newColorMap[labwareId][wellId] = colorMap[labwareId][wellId];

        continue;
      }

      if (!(wellId in newColorMap[labwareId])) {
        newColorMap[labwareId][wellId] = colorMap[labwareId][wellId];
      }

      if (background) {
        newColorMap[labwareId][wellId].background = unhighlightedTaskWellColor;
      }

      if (border) {
        newColorMap[labwareId][wellId].border = unhighlightedTaskWellColor;
      }
    }
  }

  return newColorMap;
};

export const getAllWellStyleColorMapFromTasks = (runtimeMode) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  let allColorMap = {};
  const selectedCardColorMaps = [];
  const otherCardColorMaps = [];

  const cardsArray = Object.values(cards);

  for (let i = 0, ii = cardsArray.length; i < ii; i += 1) {
    const card = cardsArray[i];

    if (card.isIgnored && !card.isSelected) {
      continue;
    }

    if (card.type === CardType.DELAY || card.type === CardType.CONFIRMATION) {
      continue;
    }

    const cardTasks = Object.values(tasks).filter((task) => card.tasks?.includes(task.ID));

    const sampleTask = cardTasks.find(
      (task) =>
        task.type === TaskType.PICK_TIP ||
        task.type === TaskType.ASPIRATE ||
        task.type === TaskType.DISPENSE ||
        task.type === TaskType.MIXING,
    );

    const sampleTask2 = cardTasks.find(
      (task) => !(task.rowTipCount === sampleTask?.rowTipCount && task.columnTipCount === sampleTask?.columnTipCount),
    );

    let tipChannel = '';

    if (sampleTask && !sampleTask2 && card.type !== CardType.SERIAL_DILUTION) {
      tipChannel = taskListBusinessAction.getTipChannelByTipCount(sampleTask.rowTipCount, sampleTask.columnTipCount);
    }

    const colorMap = dispatch(getWellStyleColorMapFromTasks(card.ID, tipChannel, runtimeMode));

    if (card.isSelected) {
      selectedCardColorMaps.push(colorMap);

      continue;
    }

    otherCardColorMaps.push(colorMap);
  }

  for (let i = 0, ii = otherCardColorMaps.length; i < ii; i += 1) {
    allColorMap = mergeColorMap(allColorMap, otherCardColorMaps[i]);
  }

  for (let i = 0; i < selectedCardColorMaps.length; i += 1) {
    const selectedCardColorMap = selectedCardColorMaps[i];
    allColorMap = mergeColorMap(allColorMap, selectedCardColorMap, true);
  }

  return allColorMap;
};

export const isLabwareNameAlreadyExist =
  (newName, id = null, isSameType = null, newLabware = null) =>
  (dispatch, getState) => {
    const {availableLabwareState} = getState();

    const labwareTypeCheck = (labware) => {
      // ignore type checking
      if (isSameType === null && newLabware === null) return true;

      const sameType = newLabware.colCount === labware.colCount && newLabware.colCount === labware.colCount;

      return isSameType ? sameType : !sameType;
    };

    if (id) {
      return !!find(
        availableLabwareState,
        (labware) =>
          labware.LabwareID !== id &&
          labware.name.trim().toLowerCase() === newName.trim().toLowerCase() &&
          labwareTypeCheck(labware),
      );
    }

    return !!find(
      availableLabwareState,
      (labware) => labware.name.trim().toLowerCase() === newName.trim().toLowerCase() && labwareTypeCheck(labware),
    );
  };

export const moveAndDuplicateTaskIntoQuadrant = (itemID, quadrant) => (dispatch, getState) => {
  const {cardState, labwareState} = getState();
  const {tasks} = cardState;

  const quadrantShifter = {
    1: {row: 0, column: 0},
    2: {row: 0, column: 1},
    3: {row: 1, column: 1},
    4: {row: 1, column: 0},
  };

  const shiftWell = (row, column, q) => {
    const newRow = row * 2 - 1 + quadrantShifter[q].row;
    const newColumn = column * 2 - 1 + quadrantShifter[q].column;

    return [newRow, newColumn];
  };

  const choosenQuadrant = Object.keys(quadrant).filter((key) => quadrant[key]);
  const newLabware = labwareState[itemID];
  const existTasks = filterToArray(tasks, {labwareID: newLabware.LabwareID});
  const movedTasks = [];

  // move tasks
  const firstQuadrant = choosenQuadrant.shift();

  existTasks.forEach((task) => {
    let newRow = convertRowToNumeric(task.row);
    let newColumn = task.column;
    [newRow, newColumn] = shiftWell(newRow, newColumn, firstQuadrant);

    movedTasks.push({
      ...task,
      row: convertRowToAlphabet(newRow),
      column: newColumn,
    });
  });

  batch(() => {
    // duplicate tasks
    if (choosenQuadrant.length) {
      dispatch(
        taskListBusinessAction.handlePlateSwapDuplicateInSimpleTransfer(
          newLabware.LabwareID,
          choosenQuadrant,
          quadrantShifter,
        ),
      );
      dispatch(handlePlateSwapDuplicateInBroadcast(newLabware.LabwareID, choosenQuadrant, quadrantShifter));
      dispatch(handlePlateSwapDuplicateInPooling(newLabware.LabwareID, choosenQuadrant, quadrantShifter));
      dispatch(handlePlateSwapDuplicateInMixing(newLabware.LabwareID, choosenQuadrant, shiftWell));
    }

    dispatch(cardStateAction.batchRestoreCardTaskStateById(movedTasks));
  });
};

export const moveAndMergeTaskIntoNonQuadrantBase = (itemID, defaultQuadrant) => (dispatch, getState) => {
  const shiftWell = (row, column) => {
    let newRow = row % 2 === 1 ? row : row - 1;
    newRow = Math.ceil(newRow / 2);

    let newColumn = column % 2 === 1 ? column : column - 1;
    newColumn = Math.ceil(newColumn / 2);

    return [newRow, newColumn];
  };
  const {cardState, labwareState, protocolState} = getState();
  const {tasks, cards} = cardState;
  const newLabware = labwareState[itemID];
  const pickTipEjectTip = [TaskType.PICK_TIP, TaskType.EJECT_TIP];
  const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

  batch(() => {
    // broadcast
    const processedIdsBroadcast = dispatch(
      handlePlateSwapMergeInBroadcast(newLabware.LabwareID, shiftWell, defaultQuadrant),
    );

    // pooling
    const processedIdsPooling = dispatch(
      handlePlateSwapMergeInPooling(newLabware.LabwareID, shiftWell, defaultQuadrant),
    );

    const processedIds = [...processedIdsBroadcast, ...processedIdsPooling];

    // other type

    const remainingCards = filterToArray(cards, (c) => c.type !== CardType.POOLING && c.type !== CardType.BROADCAST);
    remainingCards.forEach((card) => {
      const existTasks = filterToArray(
        tasks,
        (task) =>
          !processedIds.includes(task.ID) && task.labwareID === newLabware.LabwareID && card.tasks.includes(task.ID),
      );

      const removedTasksIds = [];
      const movedTasks = [];
      existTasks.forEach((task) => {
        let newRow = convertRowToNumeric(task.row);
        let newColumn = task.column;
        const quadrant = getQuadrant(newRow, newColumn);

        if (defaultQuadrant && quadrant === defaultQuadrant) {
          [newRow, newColumn] = shiftWell(newRow, newColumn);

          movedTasks.push({
            ...task,
            row: convertRowToAlphabet(newRow),
            column: newColumn,
          });
        } else {
          removedTasksIds.push(task.ID);
        }
      });

      let removedCard = true;
      if (removedTasksIds.length) {
        if (card.type === CardType.MIXING) {
          dispatch(cardStateAction.removeCard(card.ID));
        } else {
          const taskDiff = card.tasks.filter((t) => !removedTasksIds.includes(t));
          const taskOne = tasks[taskDiff[0]];
          const taskTwo = tasks[taskDiff[1]];
          if (
            taskDiff.length === 2 &&
            pickTipEjectTip.includes(taskOne?.type) &&
            pickTipEjectTip.includes(taskTwo?.type)
          ) {
            dispatch(cardStateAction.removeCard(card.ID));
          } else if (taskDiff.length === 1 && pickTipEjectTip.includes(taskOne?.type)) {
            dispatch(cardStateAction.removeCard(card.ID));
          } else {
            dispatch(cardStateAction.removeCardTasks(card.ID, removedTasksIds));
            removedCard = false;
          }
        }
      }

      dispatch(cardStateAction.batchRestoreCardTaskStateById(movedTasks));

      if (card.type === CardType.SIMPLE_TRANSFER && !removedCard) {
        // update number
        const {cardState: cardStateAfter} = getState();
        const {cards: newCards} = cardStateAfter;
        let count = 1;
        // eslint-disable-next-line no-restricted-syntax
        for (const id of newCards[card.ID].tasks) {
          dispatch(cardStateAction.setTaskNumber(id, count));
          count += 1;
        }
        dispatch(
          taskListBusinessAction.checkInvalidTask(
            card.ID,
            false,
            true,
            false,
            false,
            protocolTrayType === TrayType.HIGH_VOLUME_CADDY,
          ),
        );
      }
    });
  });
};

export const isLabwareAlreadyExist = (labware) => (dispatch, getState) => {
  const {availableLabwareState} = getState();

  return find(availableLabwareState, (e) => isSameLabwareHash(e, labware));
};

export const getWellStyleColorMapForNormalizationSelectedPlate = () => (dispatch, getState) => {
  const state = getState();
  const {labwareState, normalizationState} = state;
  const {type, sampleLabwareID, diluentLabwareID, diluentWell, currentWizardView} = normalizationState;

  const taskWellColorMap = {};

  if (!sampleLabwareID && !diluentLabwareID) {
    return taskWellColorMap;
  }

  const sampleLabware = find(labwareState, {LabwareID: sampleLabwareID});
  const diluentLabware = find(labwareState, {LabwareID: diluentLabwareID});

  const {row: normalizedRow, column: normalizedColumn} = diluentWell;

  switch (currentWizardView) {
    case NormalizationStep.SET_SAMPLE_LABWARE:
      taskWellColorMap[sampleLabwareID] = dispatch(
        getWellStyleColor(
          sampleLabwareID,
          1,
          1,
          '#50B46D',
          sampleLabware?.rowCount,
          sampleLabware?.colCount,
          null,
          type === NormalizationType.FIXED_DILUENT_VOLUME,
          null,
          null,
          null,
          null,
          true,
          false,
        ),
      );
      break;
    case NormalizationStep.SET_DILUENT_LABWARE: {
      if (!sampleLabware || !diluentLabware) {
        break;
      }
      let isSameTypePlate =
        sampleLabware.rowCount >= diluentLabware.rowCount && sampleLabware.colCount >= diluentLabware.colCount;
      let selectedRow = 1;
      let selectedColumn = 1;
      let {rowCount, colCount} = sampleLabware;
      if (type === NormalizationType.FIXED_SAMPLE_VOLUME) {
        // eslint-disable-next-line prefer-destructuring
        selectedRow = normalizedRow;
        selectedColumn = normalizedColumn;
        rowCount = 1;
        colCount = 1;
        isSameTypePlate = true;
      } else if (type === NormalizationType.FIXED_DILUENT_VOLUME && !isSameTypePlate) {
        selectedRow = normalizedRow % 2 === 0 ? 2 : 1;
        selectedColumn = normalizedColumn % 2 === 0 ? 2 : 1;
      }

      taskWellColorMap[diluentLabwareID] = dispatch(
        getWellStyleColor(
          diluentLabwareID,
          selectedRow,
          selectedColumn,
          '#50B46D',
          rowCount,
          colCount,
          null,
          type === NormalizationType.FIXED_SAMPLE_VOLUME,
          null,
          null,
          isSameTypePlate ? null : 1,
          isSameTypePlate ? null : 1,
          true,
          !isSameTypePlate,
        ),
      );
      break;
    }
    default:
      break;
  }
  return taskWellColorMap;
};

export const removeAllVirtualTrayLabwares = () => (dispatch, getState) => {
  const state = getState();
  const {labwareState} = state;

  batch(() => {
    Object.keys(labwareState).forEach((labwarePos) => {
      const labware = labwareState[labwarePos];

      if (labware.LabwareID) {
        dispatch(labwareStateAction.removeLabware(labwarePos));
      }
    });
  });
};

export const isLabwareTroughById = (labwareId) => (dispatch, getState) => {
  const {labwareState} = getState();
  return isLabwareTrough(Object.values(labwareState).find((labware) => labware.LabwareID === labwareId));
};
